// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT ACTION MODAL — pickers de liaison (Projets / Comités / Organisation / Plans)
// ═══════════════════════════════════════════════════════════════════════════
//
// Extrait de `renderActionEditor` (index.ts) le 2026-09-27, méthode « point 16 » :
// les quatre listes à cases du bloc central, le résumé affiché au repli et le
// repli lui-même. Les rendus (rangée, listes filtrées, résumé) sont des
// fonctions PURES, testées dans `link-pickers.test.ts` ; `wireLinkPickers` ne
// fait que le câblage DOM (chargements async, recherche, cases, escalade) et
// rend les Sets vivants des sélections (`LinkPickersSelection`), que le save
// côté index.ts lit comme source de vérité.
//
// Les 4 listes partagent la même ligne « code + nom tronqué » et le même
// tronquage (dette #6b). Les divergences (classe de label des structures,
// bouton d'escalade, code composite des phases) passent par options.
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, loadCommittees
// + window.api.

export interface ProjectOption {
  id: number;
  code?: string | null;
  name: string;
}

export interface CommitteeRow {
  id: number;
  code: string;
  name: string;
  position?: number;
}

export interface StructureRow {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
  parent_ids?: number[];
}

export interface PhaseRow {
  id: number;
  code: string;
  title: string;
  plan_id: number;
  plan_code: string;
  plan_title: string;
  plan_active?: boolean;
  // Phase Intelligente : exclue du picker (aucun lien manuel possible — ses
  // actions sont calculées par critères).
  is_smart?: boolean;
}

/** Les liaisons initiales de l'action, seule partie lue par les pickers. */
export interface LinkPickersAction {
  project_ids?: number[];
  committee_ids?: number[];
  structure_ids?: number[];
  escalated_structure_ids?: number[];
  phase_ids?: number[];
}

/** Les sélections vivantes des pickers à recherche : Sets mis à jour à chaque
 *  case cochée/décochée, rendus par `wireLinkPickers` et lus par le save de
 *  l'éditeur comme source de vérité — le DOM ne voit que les cases non
 *  filtrées. Les comités, sans recherche, se lisent directement dans le DOM
 *  (`.ea-committee-chk:checked`). */
export interface LinkPickersSelection {
  projectIds: ReadonlySet<number>;
  structureIds: ReadonlySet<number>;
  /** Sous-ensemble de `structureIds` dont le lien remonte aux parents directs. */
  escalatedStructureIds: ReadonlySet<number>;
  phaseIds: ReadonlySet<number>;
}

// ── Rendus purs ────────────────────────────────────────────────────────────

/** Tronque `s` à `n` caractères avec ellipse (helper commun aux pickers). */
export function truncate20(s: string, n = 20): string {
  return s.length > n ? s.substring(0, n - 1) + '…' : s;
}

/** Rend une ligne de case à cocher « code + nom tronqué » d'un picker de
 *  référentiel. `extra` = HTML inséré avant `</label>` (ex. bouton escalade) ;
 *  `labelClass` = classe(s) ajoutée(s) au `<label>` (ex. ligne structure). */
export function renderRefCheckRow(o: {
  id: number;
  cssClass: string;
  checked: boolean;
  code: string;
  fullText: string;
  shortText: string;
  labelClass?: string;
  extra?: string;
}): string {
  const cls = o.labelClass ? ` class="${o.labelClass}"` : '';
  return (
    `<label${cls} style="display:flex;align-items:center;gap:4px;font-size:11px;cursor:pointer;min-width:0">` +
    `<input type="checkbox" class="${o.cssClass}" value="${o.id}"${o.checked ? ' checked' : ''}>` +
    `<span title="${esc(o.fullText)}" style="font-size:10px;color:var(--text3);flex-shrink:0;cursor:help">${esc(o.code)}</span>` +
    `<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0">${esc(o.shortText)}</span>` +
    `${o.extra ?? ''}</label>`
  );
}

/** Message occupant toute la largeur de la grille d'un picker (liste vide,
 *  aucun résultat de recherche). */
function _gridMessageHtml(i18nKey: string): string {
  return `<span style="grid-column:1 / -1;color:var(--text3);font-size:11px">${esc(t(i18nKey))}</span>`;
}

/** Message d'échec de chargement d'un référentiel. */
function _unavailableHtml(i18nKey: string): string {
  return `<span style="color:var(--warn);font-size:11px">${esc(t(i18nKey))}</span>`;
}

/** Lignes du picker projets, filtrées sur `filter` (déjà en minuscules,
 *  sans espaces autour ; vide = tout). Libellés tronqués à 20 chars
 *  (RefactoUX/Actions 2026-05-20) pour éviter le chevauchement entre les 2
 *  sous-colonnes du picker en modale ; le nom complet reste accessible via le
 *  `title=` sur le span « code ». */
export function renderProjectRows(
  projects: readonly ProjectOption[],
  picked: ReadonlySet<number>,
  filter = '',
): string {
  const pool = projects.filter((p) => {
    if (!filter) return true;
    return p.name.toLowerCase().includes(filter) || (p.code || '').toLowerCase().includes(filter);
  });
  if (pool.length === 0) return _gridMessageHtml('actionModal.projects.noResults');
  return pool
    .map((p) =>
      renderRefCheckRow({
        id: p.id,
        cssClass: 'ea-proj-chk',
        checked: picked.has(p.id),
        code: p.code || '—',
        fullText: p.name || '',
        shortText: truncate20(p.name || ''),
      }),
    )
    .join('');
}

/** Lignes du picker comités, triées par position puis code. Vide → libellé
 *  « aucun comité ». Même traitement que les projets : libellé tronqué à 20
 *  chars, nom complet via `title=` au survol du code. */
export function renderCommitteeRows(
  committees: readonly CommitteeRow[] | null | undefined,
  checkedIds: readonly number[],
): string {
  if (!committees || committees.length === 0) {
    return _gridMessageHtml('actionModal.committees.empty');
  }
  return committees
    .slice()
    .sort((x, y) => (x.position || 0) - (y.position || 0) || x.code.localeCompare(y.code))
    .map((c) =>
      renderRefCheckRow({
        id: c.id,
        cssClass: 'ea-committee-chk',
        checked: checkedIds.includes(c.id),
        code: c.code,
        fullText: c.name || '',
        shortText: truncate20(c.name || ''),
      }),
    )
    .join('');
}

/** Structures proposées au picker : seules les ACTIVES, triées par code. */
export function linkableStructures(
  rows: readonly StructureRow[] | null | undefined,
): StructureRow[] {
  return (rows || [])
    .filter((s) => s.is_active !== false)
    .sort((x, y) => x.code.localeCompare(y.code));
}

/** Lignes du picker organisation, filtrées sur `filter` (minuscules, sans
 *  espaces autour). `escalated` = sous-ensemble des structures dont le lien
 *  remonte aux parents directs (toggle ↑, repris du geste des risques). */
export function renderStructureRows(
  structures: readonly StructureRow[],
  picked: ReadonlySet<number>,
  escalated: ReadonlySet<number>,
  filter = '',
): string {
  const pool = structures.filter((s) => {
    if (!filter) return true;
    return (
      (s.label || '').toLowerCase().includes(filter) ||
      (s.code || '').toLowerCase().includes(filter)
    );
  });
  if (pool.length === 0) return _gridMessageHtml('actionModal.structures.noResults');
  return pool
    .map((s) => {
      const fullName = s.label || '';
      const shortName = truncate20(fullName);
      // Le ↑ d'escalade n'a de sens que si la structure a des parents ET
      // qu'elle est sélectionnée (on n'escalade qu'un lien existant).
      const hasParents = (s.parent_ids || []).length > 0;
      const checked = picked.has(s.id);
      const escOn = checked && escalated.has(s.id);
      const escTitle = !checked
        ? t('actionModal.structures.escalateNeedsLink')
        : escOn
          ? t('actionModal.structures.escalateOff')
          : t('actionModal.structures.escalate');
      const escBtn = hasParents
        ? `<button type="button" class="ea-struct-esc${escOn ? ' on' : ''}" data-sid="${s.id}" ${checked ? '' : 'disabled'} aria-pressed="${escOn ? 'true' : 'false'}" title="${esc(escTitle)}">↑</button>`
        : '';
      return renderRefCheckRow({
        id: s.id,
        cssClass: 'ea-structure-chk',
        checked,
        code: s.code,
        fullText: fullName,
        shortText: shortName,
        labelClass: `ea-struct-row${escOn ? ' esc-on' : ''}`,
        extra: escBtn,
      });
    })
    .join('');
}

/** Phases proposées au picker : plans ACTIFS, hors phases Intelligentes (pas
 *  de liaison manuelle d'action), triées par code de plan puis code. */
export function linkablePhases(rows: readonly PhaseRow[] | null | undefined): PhaseRow[] {
  return (rows || [])
    .filter((p) => p.plan_active !== false && !p.is_smart)
    .sort(
      (x, y) =>
        (x.plan_code || '').localeCompare(y.plan_code || '') || x.code.localeCompare(y.code),
    );
}

/** Lignes du picker plans d'action, filtrées sur `filter` (minuscules, sans
 *  espaces autour) : titre, code de phase ou code de plan. */
export function renderPhaseRows(
  phases: readonly PhaseRow[],
  picked: ReadonlySet<number>,
  filter = '',
): string {
  const pool = phases.filter((p) => {
    if (!filter) return true;
    return (
      (p.title || '').toLowerCase().includes(filter) ||
      (p.code || '').toLowerCase().includes(filter) ||
      (p.plan_code || '').toLowerCase().includes(filter)
    );
  });
  if (pool.length === 0) return _gridMessageHtml('actionModal.phases.noResults');
  return pool
    .map((p) =>
      renderRefCheckRow({
        id: p.id,
        cssClass: 'ea-phase-chk',
        checked: picked.has(p.id),
        code: `${p.plan_code}/${p.code}`,
        fullText: `${p.plan_code} · ${p.title}`,
        shortText: truncate20(p.title || ''),
      }),
    )
    .join('');
}

/** Ce que le résumé au repli a besoin de connaître : les référentiels chargés
 *  et les identifiants sélectionnés de chaque liste. */
export interface LinkPickersSummaryInput {
  projects: readonly ProjectOption[];
  committees: readonly CommitteeRow[];
  structures: readonly StructureRow[];
  phases: readonly PhaseRow[];
  projectIds: ReadonlySet<number>;
  committeeIds: ReadonlySet<number>;
  structureIds: ReadonlySet<number>;
  phaseIds: ReadonlySet<number>;
}

function _codeChipHtml(code: string, label: string): string {
  return `<span title="${esc(label)}" style="font-size:10px;font-family:var(--font-mono);color:var(--text2);background:var(--bg3);border:1px solid var(--border);border-radius:8px;padding:0 6px;cursor:help">${esc(code)}</span>`;
}

/** Résumé des valeurs sélectionnées d'un bloc (affiché sous le titre au
 *  repli) : une puce « code » par valeur, libellé complet au survol. Aucune
 *  sélection → libellé i18n « aucune ». Une clé inconnue → idem. */
export function renderSelectedSummaryHtml(key: string, s: LinkPickersSummaryInput): string {
  let chips: string[] = [];
  if (key === 'projects') {
    chips = s.projects
      .filter((p) => s.projectIds.has(p.id))
      .map((p) => _codeChipHtml(p.code || '—', p.name || ''));
  } else if (key === 'committees') {
    chips = s.committees
      .filter((c) => s.committeeIds.has(c.id))
      .map((c) => _codeChipHtml(c.code, c.name || ''));
  } else if (key === 'structures') {
    chips = s.structures
      .filter((x) => s.structureIds.has(x.id))
      .map((x) => _codeChipHtml(x.code, x.label || ''));
  } else if (key === 'phases') {
    chips = s.phases
      .filter((p) => s.phaseIds.has(p.id))
      .map((p) => _codeChipHtml(`${p.plan_code}/${p.code}`, `${p.plan_code} · ${p.title}`));
  }
  return chips.length
    ? chips.join('')
    : `<span style="font-size:10px;color:var(--text3);font-style:italic">${esc(t('actionModal.collapse.none'))}</span>`;
}

// ── Câblage DOM ────────────────────────────────────────────────────────────

export interface WireLinkPickersInput {
  /** L'action éditée (prefill déjà fusionné) : liaisons initiales. */
  action: LinkPickersAction;
  /** Référentiel projets fourni par l'hôte (rendu synchrone). */
  projects: ProjectOption[];
  /** Réaction générique de l'éditeur à une modification (bascule des CTA +
   *  auto-note), déclenchée à chaque case cochée/décochée et à chaque toggle
   *  d'escalade. */
  onFieldChange: () => void;
}

/**
 * Câble les quatre pickers de liaison et le repli de leurs blocs. Un picker
 * absent du DOM (éditeur rendu partiellement, tests unitaires) n'est pas câblé
 * mais sa sélection initiale est rendue quand même.
 *
 * Renvoie les Sets vivants des sélections : le save de l'éditeur les lit comme
 * source de vérité, au lieu de scanner le DOM qui ne voit que les cases
 * actuellement filtrées.
 */
export function wireLinkPickers(input: WireLinkPickersInput): LinkPickersSelection {
  const { action, projects, onFieldChange } = input;

  // Sets vivants — mis à jour à chaque check/uncheck pour qu'un re-render
  // après changement de filtre conserve les sélections (bug avril 2026 :
  // avant, la liste figée perdait les cocheries quand on effaçait la
  // recherche).
  const currentPids = new Set<number>(action.project_ids || []);
  const currentSids = new Set<number>(action.structure_ids || []);
  // Escalade : sous-ensemble des structures dont le lien remonte aux parents
  // directs (toggle ↑, repris du geste des risques).
  const currentEsc = new Set<number>(action.escalated_structure_ids || []);
  const currentPhaseIds = new Set<number>(action.phase_ids || []);

  // Render project checkboxes in picker
  const projPicker = document.getElementById('ea-projects-picker');
  const projSearch = document.getElementById('ea-proj-search') as HTMLInputElement | null;
  if (projPicker) {
    projPicker.innerHTML = renderProjectRows(projects, currentPids);
    // Délégation : suit les check/uncheck pour mettre à jour l'état vivant
    // + bascule les CTA + déclenche l'auto-note.
    projPicker.addEventListener('change', (e: Event) => {
      const cb = e.target as HTMLInputElement | null;
      if (!cb || !cb.classList.contains('ea-proj-chk')) return;
      const id = parseInt(cb.value);
      if (cb.checked) currentPids.add(id);
      else currentPids.delete(id);
      onFieldChange();
    });
    projSearch?.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      projPicker.innerHTML = renderProjectRows(
        projects,
        currentPids,
        (target?.value ?? '').toLowerCase().trim(),
      );
    });
  }

  // Données chargées conservées pour le résumé affiché au repli des blocs.
  let _committeesLoaded: CommitteeRow[] = [];
  let _structuresLoaded: StructureRow[] = [];
  let _phasesLoaded: PhaseRow[] = [];

  // ── Résumé des valeurs sélectionnées (affiché sous le titre au repli) ──
  // Défini avant les pickers pour être appelable après chaque chargement
  // async (comités/structures/phases) : un bloc rendu replié à l'ouverture
  // doit rafraîchir son résumé une fois son référentiel arrivé, sinon il
  // resterait vide le temps du fetch.
  const _selectedSummary = (key: string): string =>
    renderSelectedSummaryHtml(key, {
      projects,
      committees: _committeesLoaded,
      structures: _structuresLoaded,
      phases: _phasesLoaded,
      projectIds: currentPids,
      committeeIds: new Set(
        Array.from(document.querySelectorAll<HTMLInputElement>('.ea-committee-chk:checked')).map(
          (c) => parseInt(c.value),
        ),
      ),
      structureIds: currentSids,
      phaseIds: currentPhaseIds,
    });
  // Remplit le résumé d'un bloc s'il est actuellement replié (no-op sinon,
  // ex. en modale où les blocs restent dépliés).
  const _applyCollapsedSummary = (key: string): void => {
    const toggle = document.querySelector(`.ea-collapse-toggle[data-collapse="${key}"]`);
    if (toggle?.getAttribute('aria-expanded') !== 'false') return;
    const summary = document.querySelector<HTMLElement>(`[data-summary="${key}"]`);
    if (summary) summary.innerHTML = _selectedSummary(key);
  };

  // ── Picker comités (M2M) ──────────────────────────────────────────────
  const commPicker = document.getElementById('ea-committees-picker');
  if (commPicker) {
    const currentCids = action.committee_ids || [];
    void loadCommittees()
      .then((committees) => {
        _committeesLoaded = committees as CommitteeRow[];
        commPicker.innerHTML = renderCommitteeRows(_committeesLoaded, currentCids);
        _applyCollapsedSummary('committees');
      })
      .catch(() => {
        commPicker.innerHTML = _unavailableHtml('actionModal.committees.unavailable');
      });
  }

  // ── Picker organisation / structures (M2M) ────────────────────────────
  // Même UX que les projets : recherche + Set vivant pour ne pas perdre les
  // cases cochées masquées par le filtre. Seules les structures ACTIVES sont
  // proposées ; code en survol → libellé via title=.
  const structPicker = document.getElementById('ea-structures-picker');
  const structSearch = document.getElementById('ea-struct-search') as HTMLInputElement | null;
  if (structPicker) {
    void (api.getStructuresIfAllowed() as Promise<StructureRow[]>)
      .then((rows) => {
        _structuresLoaded = linkableStructures(rows);
        if (!_structuresLoaded.length) {
          structPicker.innerHTML = _gridMessageHtml('actionModal.structures.empty');
          return;
        }
        structPicker.innerHTML = renderStructureRows(_structuresLoaded, currentSids, currentEsc);
        _applyCollapsedSummary('structures');
      })
      .catch(() => {
        structPicker.innerHTML = _unavailableHtml('actionModal.structures.unavailable');
      });
    structPicker.addEventListener('change', (e: Event) => {
      const cb = e.target as HTMLInputElement | null;
      if (!cb || !cb.classList.contains('ea-structure-chk')) return;
      const id = parseInt(cb.value);
      const label = cb.closest('label');
      const escBtn = label?.querySelector('.ea-struct-esc') as HTMLButtonElement | null;
      if (cb.checked) {
        currentSids.add(id);
        // La structure est liée → on peut désormais l'escalader.
        if (escBtn) {
          escBtn.disabled = false;
          escBtn.title = t('actionModal.structures.escalate');
        }
      } else {
        currentSids.delete(id);
        // Décocher = retirer le lien → l'escalade n'a plus de sens, on l'efface.
        currentEsc.delete(id);
        if (escBtn) {
          escBtn.disabled = true;
          escBtn.classList.remove('on');
          escBtn.setAttribute('aria-pressed', 'false');
          escBtn.title = t('actionModal.structures.escalateNeedsLink');
        }
        label?.classList.remove('esc-on');
      }
      onFieldChange();
    });
    // Toggle ↑ d'escalade (clic) : bascule l'état + couleur, sans re-render.
    structPicker.addEventListener('click', (e: Event) => {
      const btn = (e.target as HTMLElement | null)?.closest('.ea-struct-esc');
      if (!(btn instanceof HTMLElement)) return;
      const sid = parseInt(btn.dataset['sid'] || '');
      if (Number.isNaN(sid)) return;
      if (currentEsc.has(sid)) currentEsc.delete(sid);
      else currentEsc.add(sid);
      const on = currentEsc.has(sid);
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.title = on
        ? t('actionModal.structures.escalateOff')
        : t('actionModal.structures.escalate');
      btn.closest('label')?.classList.toggle('esc-on', on);
      onFieldChange();
    });
    structSearch?.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      structPicker.innerHTML = renderStructureRows(
        _structuresLoaded,
        currentSids,
        currentEsc,
        (target?.value ?? '').toLowerCase().trim(),
      );
    });
  }

  // ── Picker plans d'action / phases (M2M) ──────────────────────────────
  // Même UX que l'organisation, sans escalade (les phases n'en ont pas).
  // Set vivant pour préserver les cases cochées masquées par la recherche.
  // Référentiel OUVERT (/action-plans/phases/options) : phases des plans ACTIFS.
  const phasePicker = document.getElementById('ea-phases-picker');
  const phaseSearch = document.getElementById('ea-phase-search') as HTMLInputElement | null;
  if (phasePicker) {
    void (api.getPhasesForFilter() as Promise<PhaseRow[]>)
      .then((rows) => {
        _phasesLoaded = linkablePhases(rows);
        if (!_phasesLoaded.length) {
          phasePicker.innerHTML = _gridMessageHtml('actionModal.phases.empty');
          return;
        }
        phasePicker.innerHTML = renderPhaseRows(_phasesLoaded, currentPhaseIds);
        _applyCollapsedSummary('phases');
      })
      .catch(() => {
        phasePicker.innerHTML = _unavailableHtml('actionModal.phases.unavailable');
      });
    phasePicker.addEventListener('change', (e: Event) => {
      const cb = e.target as HTMLInputElement | null;
      if (!cb || !cb.classList.contains('ea-phase-chk')) return;
      const id = parseInt(cb.value);
      if (cb.checked) currentPhaseIds.add(id);
      else currentPhaseIds.delete(id);
      onFieldChange();
    });
    phaseSearch?.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      phasePicker.innerHTML = renderPhaseRows(
        _phasesLoaded,
        currentPhaseIds,
        (target?.value ?? '').toLowerCase().trim(),
      );
    });
  }

  // ── Repli des blocs Projets / Comités / Organisation / Plans ──────────
  // Au repli : on masque le corps et on affiche sous le titre les codes des
  // valeurs sélectionnées (via _selectedSummary, défini plus haut). Au
  // déploiement : l'inverse. Le résumé est recalculé à chaque repli (le
  // corps masqué ne peut plus changer la sélection).
  const projcom = document.querySelector('.ea-block-projcom');
  projcom?.addEventListener('click', (e: Event) => {
    const toggle = (e.target as HTMLElement | null)?.closest('.ea-collapse-toggle');
    if (!(toggle instanceof HTMLElement)) return;
    const key = toggle.dataset['collapse'] || '';
    const body = projcom.querySelector<HTMLElement>(`[data-body="${key}"]`);
    const summary = projcom.querySelector<HTMLElement>(`[data-summary="${key}"]`);
    const caret = toggle.querySelector<HTMLElement>('.ea-collapse-caret');
    if (!body || !summary) return;
    const collapsed = body.style.display !== 'none';
    if (collapsed) {
      summary.innerHTML = _selectedSummary(key);
      summary.style.display = 'flex';
      body.style.display = 'none';
    } else {
      summary.style.display = 'none';
      body.style.display = '';
    }
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    if (caret) caret.style.transform = collapsed ? 'rotate(-90deg)' : 'rotate(0deg)';
  });

  // Blocs rendus repliés à l'ouverture (option page) : remplir leur résumé
  // au boot. Les projets sont déjà chargés (rendu synchrone) ; comités /
  // structures / phases seront rafraîchis par leur chargement async.
  (['projects', 'committees', 'structures', 'phases'] as const).forEach(_applyCollapsedSummary);

  return {
    projectIds: currentPids,
    structureIds: currentSids,
    escalatedStructureIds: currentEsc,
    phaseIds: currentPhaseIds,
  };
}
